import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { format as formatDateFns, parse as parseDateFns } from 'date-fns'
import type { ReactNode } from 'react'
import { ApiError } from '@/api/client'
import { statusColor } from '@/theme'

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
})

const preciseCurrencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
})

export function formatMoney(value?: number | null, precise = false) {
  if (value === undefined || value === null) return '—'
  return precise ? preciseCurrencyFormatter.format(value) : currencyFormatter.format(value)
}

export function formatNumber(value?: number | null) {
  if (value === undefined || value === null) return '—'
  return new Intl.NumberFormat('en-IN').format(value)
}

/**
 * A date as the office writes one: 01-01-2026.
 *
 * <p>One format on every screen, every form and every document. Two ways of
 * writing a date in one system is how "05-08" is read as August by one desk
 * and May by another, and a short month name does not help when it is the
 * only place a month is spelled.</p>
 */
export function formatDate(value?: string | null) {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
        .replace(/\//g, '-')
}

export function formatDateTime(value?: string | null) {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date
        .toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
        // en-GB writes 01/01/2026, 14:30; the office writes 01-01-2026.
        .replace(/\//g, '-')
}

/**
 * The clock time out of a full timestamp, as `07:00`.
 *
 * <p>For saying when something runs on a day that has already been named.
 * Repeating the date beside the hours makes a line nobody reads to the end.</p>
 */
export function formatTimeOfDay(value?: string | null) {
  if (!value) return '—'
  const at = new Date(value)
  return Number.isNaN(at.getTime())
    ? value
    : at.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
}

/**
 * A plain `HH:mm` or `HH:mm:ss` clock reading, as the office writes it:
 * `07:00 AM`. Used wherever a bare time string - not a full timestamp - needs
 * the same 12-hour reading `formatTimeOfDay` gives a `Date`.
 */
function format12Hour(raw: string) {
  const [h, m] = raw.split(':')
  const hour = Number(h)
  if (Number.isNaN(hour) || m === undefined) return raw
  const period = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour % 12 === 0 ? 12 : hour % 12
  return `${String(hour12).padStart(2, '0')}:${m} ${period}`
}

/**
 * A slot's hours as `07:00 AM–03:30 PM`. The API sends LocalTime as
 * `HH:mm:ss`; the seconds are always zero and only make the label harder to
 * scan.
 */
export function formatSlotHours(startTime?: string | null, endTime?: string | null) {
  if (!startTime || !endTime) return '—'
  return `${format12Hour(startTime.slice(0, 5))}–${format12Hour(endTime.slice(0, 5))}`
}

/**
 * A mobile number as `98400 12345`.
 *
 * <p>Five and five is how an Indian number is read aloud and written down, and
 * a run of ten digits is genuinely hard to check against a phone screen. A
 * country code is kept in front, and anything that is not a ten digit number -
 * a landline, a partly typed number, a masked one - is left exactly as it came
 * rather than being mangled into a shape it does not have.</p>
 */
export function formatMobile(value?: string | null) {
  if (!value) return '—'
  const trimmed = value.trim()
  const country = trimmed.startsWith('+') ? trimmed.slice(0, 3) : ''
  const digits = (country ? trimmed.slice(country.length) : trimmed).replace(/\D/g, '')
  if (digits.length !== 10) {
    return trimmed
  }
  return `${country ? `${country} ` : ''}${digits.slice(0, 5)} ${digits.slice(5)}`
}

/**
 * A fresh identifier, wherever the app happens to be served from.
 *
 * <p>{@code crypto.randomUUID} exists only in a secure context - HTTPS, or
 * localhost. Opened over plain HTTP on the office network it is undefined, and
 * calling it threw while a screen was rendering, which took the whole page down
 * rather than the one field that wanted an id. The fallback is not a v4 UUID
 * and is not meant to be: it is a one-off key for a request, unique enough that
 * two clicks a moment apart cannot collide.</p>
 */
export function newId() {
  const api = globalThis.crypto
  if (api && typeof api.randomUUID === 'function') {
    return api.randomUUID()
  }
  if (api && typeof api.getRandomValues === 'function') {
    const bytes = api.getRandomValues(new Uint8Array(16))
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 14)}`
}

/** Turns SCREAMING_SNAKE statuses into readable words. */
export function humanise(value?: string | null) {
  if (!value) return '—'
  return value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export function StatusChip({ status, size = 'small' }: { status?: string; size?: 'small' | 'medium' }) {
  return <Chip label={humanise(status)} color={statusColor(status)} size={size} variant="filled" />
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string
  subtitle?: string
  actions?: ReactNode
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      spacing={2}
      sx={{ mb: 3 }}
    >
      <Box>
        <Typography variant="h5">{title}</Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && <Stack direction="row" spacing={1}>{actions}</Stack>}
    </Stack>
  )
}

export function MetricCard({
  label,
  value,
  caption,
  tone = 'default',
}: {
  label: string
  value: ReactNode
  caption?: string
  tone?: 'default' | 'success' | 'warning' | 'error'
}) {
  const toneColor =
    tone === 'success'
      ? 'success.main'
      : tone === 'warning'
        ? 'warning.main'
        : tone === 'error'
          ? 'error.main'
          : 'text.primary'

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
          {label}
        </Typography>
        <Typography variant="h5" sx={{ mt: 0.5, color: toneColor }}>
          {value}
        </Typography>
        {caption && (
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

/** Consistent error rendering, including the approval hint the API can return. */
export function ErrorBanner({ error }: { error: unknown }) {
  if (!error) return null

  if (error instanceof ApiError) {
    const severity = error.needsApproval ? 'warning' : 'error'
    return (
      <Alert severity={severity} sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight={600}>
          {error.message}
        </Typography>
        {error.fieldErrors.length > 0 && (
          <Box component="ul" sx={{ pl: 2, m: 0, mt: 0.5 }}>
            {error.fieldErrors.map((fieldError) => (
              <li key={fieldError.field}>
                <Typography variant="caption">
                  {fieldError.field}: {fieldError.message}
                </Typography>
              </li>
            ))}
          </Box>
        )}
        {error.needsApproval && typeof error.details.requiredRole === 'string' && (
          <Typography variant="caption">
            Requires sign-off from {humanise(error.details.requiredRole)}.
          </Typography>
        )}
      </Alert>
    )
  }

  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      {/* A message that is blank leaves an empty red box saying nothing. */}
      {(error instanceof Error && error.message.trim()) || 'Something went wrong'}
    </Alert>
  )
}

export function LoadingBlock({ rows = 4 }: { rows?: number }) {
  return (
    <Stack spacing={1}>
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton key={index} variant="rectangular" height={44} sx={{ borderRadius: 1 }} />
      ))}
    </Stack>
  )
}

export function InlineSpinner() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress size={28} />
    </Box>
  )
}

export function EmptyState({ message, hint }: { message: string; hint?: string }) {
  return (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      <Typography variant="subtitle1" color="text.secondary">
        {message}
      </Typography>
      {hint && (
        <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </Box>
  )
}

/** Today and a horizon date as yyyy-MM-dd, used by the date filters. */
export function isoDate(offsetDays = 0) {
  const date = new Date()
  date.setDate(date.getDate() + offsetDays)
  // The day it is here, not the day it is in Greenwich. Taken off the ISO
  // string, an Indian morning before half past five reads as yesterday, and a
  // screen that opens on today would open on the day before.
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

export function isoDateTime(offsetDays = 0) {
  const date = new Date()
  date.setDate(date.getDate() + offsetDays)
  return date.toISOString()
}

/**
 * A bare clock time, picked and shown the way the office reads one: 09:30 PM
 * rather than 21:30. A native `<input type="time">` renders in whatever
 * format the browser or OS locale gives it - this always reads 12-hour,
 * on every machine, while still handing back and taking the plain `HH:mm`
 * string every form and the API already deal in.
 */
export function TimeField({
  label,
  value,
  onChange,
  helperText,
  size = 'small',
  fullWidth = true,
  disabled,
  required,
  error,
  sx,
  ariaLabel,
}: {
  label?: string
  value: string
  onChange: (value: string) => void
  helperText?: ReactNode
  size?: 'small' | 'medium'
  fullWidth?: boolean
  disabled?: boolean
  required?: boolean
  error?: boolean
  sx?: SxProps<Theme>
  ariaLabel?: string
}) {
  const picked = value ? parseDateFns(value.slice(0, 5), 'HH:mm', new Date()) : null
  return (
    <TimePicker
      label={label}
      value={picked}
      onChange={(next) => onChange(next ? formatDateFns(next, 'HH:mm') : '')}
      format="hh:mm a"
      disabled={disabled}
      slotProps={{
        textField: {
          size,
          fullWidth,
          required,
          error,
          helperText,
          sx,
          inputProps: ariaLabel ? { 'aria-label': ariaLabel } : undefined,
        },
      }}
    />
  )
}
